import { createSignal, type Accessor } from 'solid-js';
import type { RgbColor } from 'solid-color';
import tinycolor from 'tinycolor2';

const COLOR_PICKER_OFFSET_PX = 4;
const VIEWPORT_EDGE_PADDING_PX = 8;

/** Matches `ColorPickerPanel` `w-[260px]`. */
export const COLOR_PICKER_PANEL_WIDTH_PX = 260;
/** Conservative height estimate before the portaled panel has been measured. */
export const COLOR_PICKER_PANEL_HEIGHT_PX = 420;

export interface PickerPosition {
  readonly left: number;
  readonly top: number;
}

export interface PickerSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Viewport-aware popover position for a swatch-anchored colour picker.
 * Prefers below the anchor, flips above when needed, and shifts horizontally
 * to stay on screen.
 */
export function computePaletteStopPickerPosition(
  anchorRect: DOMRect,
  pickerSize: PickerSize = {
    width: COLOR_PICKER_PANEL_WIDTH_PX,
    height: COLOR_PICKER_PANEL_HEIGHT_PX,
  }
): PickerPosition {
  const padding = VIEWPORT_EDGE_PADDING_PX;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = anchorRect.left;
  let top = anchorRect.bottom + COLOR_PICKER_OFFSET_PX;

  const spaceBelow = viewportHeight - padding - top;
  const spaceAbove = anchorRect.top - padding - COLOR_PICKER_OFFSET_PX;
  if (pickerSize.height > spaceBelow && spaceAbove > spaceBelow) {
    top = anchorRect.top - pickerSize.height - COLOR_PICKER_OFFSET_PX;
  }

  if (left + pickerSize.width > viewportWidth - padding) {
    left = anchorRect.right - pickerSize.width;
  }

  left = Math.max(
    padding,
    Math.min(left, viewportWidth - pickerSize.width - padding)
  );
  top = Math.max(
    padding,
    Math.min(top, viewportHeight - pickerSize.height - padding)
  );

  return { left, top };
}

export interface PaletteStopColorPickerControls {
  readonly showColorPicker: Accessor<boolean>;
  getSwatchAnchor(): HTMLDivElement | undefined;
  setSwatchAnchor(el: HTMLDivElement): void;
  toggleColorPicker(): void;
  closeColorPicker(): void;
  /** Converts rgb to hex without closing the picker (live updates). */
  applyColor(rgb: RgbColor): string;
  saveColor(rgb: RgbColor): string;
}

export function toPickerHexColor(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

/**
 * Shared swatch-anchored colour picker state used by stop row and new-stop
 * inputs. Mutual exclusion across pickers is handled by outside-click closing
 * in `PaletteStopColorPicker` — no panel-level group coordinator needed.
 */
export function createPaletteStopColorPickerControls(): PaletteStopColorPickerControls {
  const [showColorPicker, setShowColorPicker] = createSignal(false);
  let swatchAnchor: HTMLDivElement | undefined;

  function getSwatchAnchor(): HTMLDivElement | undefined {
    return swatchAnchor;
  }

  function setSwatchAnchor(el: HTMLDivElement): void {
    swatchAnchor = el;
  }

  function toggleColorPicker(): void {
    setShowColorPicker(!showColorPicker());
  }

  function closeColorPicker(): void {
    setShowColorPicker(false);
  }

  function applyColor(rgb: RgbColor): string {
    const color = tinycolor(rgb);
    return color.getAlpha() < 1 ? color.toHex8String() : color.toHexString();
  }

  function saveColor(rgb: RgbColor): string {
    const hex = applyColor(rgb);
    closeColorPicker();
    return hex;
  }

  return {
    showColorPicker,
    getSwatchAnchor,
    setSwatchAnchor,
    toggleColorPicker,
    closeColorPicker,
    applyColor,
    saveColor,
  };
}
